import React, { useEffect, useState } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import AppBar from '@material-ui/core/AppBar';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Tooltip from '@material-ui/core/Tooltip';

import { openOrCloseGlobalDialog } from '../../action/dialog/global-dialog';
import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import { reassignEmployeeGroups } from '../../employee-groups/actions/reassign-employee-groups';
import {
  employeeGroupsForOrgIdStateSelector,
  integrationEmployeeGroupsForOrgIdSelector,
} from '../../employee-groups/employee-group-selectors';
import {
  EMPLOYEE_STATUS,
  EmployeeStatus,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { userSelector } from '../../reducers/current-user-selectors';
import {
  activeEmployeesForGroupIdSelector,
  employeeSelector,
  employeesForAccountIdSelector,
  employeesStateForOrgIdAndAllStatusSelector,
} from '../../reducers/employee-selectors';
import { orgSelector, orgsForAccountId } from '../../reducers/org-selectors';
import { RequestState } from '../../reducers/utils/state-manager';
import { loadMergeIntegration } from '../actions/load-merge-integration';
import {
  mergeIntegrationForOrgIdSelector,
  mergeIntegrationsStateForOrgIdSelector,
} from '../merge-integration-selectors';

import { GLOBAL_DIALOGS } from '../../dialogs/global-dialogs-config';
import { UiBreadcrumbHeader } from '../../ui/ui-breadcrumb-header';
import { UiButton } from '../../ui/ui-button';
import { employeesRoute } from '../../web-routes';

import { GroupAssignmentTab } from './group-assignment-tab';
import { PreviewImportTab } from './preview-import-tab';
import { ResolveConflictsTab } from './resolve-conflicts-tab';

import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps =
  ({ originOrgId }: Props) =>
  (state: any) => {
    const originOrg = orgSelector(state, originOrgId);
    const user = userSelector(state);
    return {
      mergeIntegration: mergeIntegrationForOrgIdSelector(state, originOrgId),
      mergeIntegrationState: mergeIntegrationsStateForOrgIdSelector(state, originOrgId),
      orgs: (originOrg ? orgsForAccountId(state, originOrg?.accountId) : []) || [],
      employeeById: (employeeId: number) => employeeSelector(state, employeeId),
      employeesForGroupId: (groupId: number) => activeEmployeesForGroupIdSelector(state, groupId),
      employees: originOrg ? employeesForAccountIdSelector(state, originOrg.accountId) : [],
      employeeGroups: integrationEmployeeGroupsForOrgIdSelector(state, originOrgId),
      employeesStates: employeesStateForOrgIdAndAllStatusSelector(state, originOrgId),
      employeeGroupsState: employeeGroupsForOrgIdStateSelector(state, originOrgId),
      user,
    };
  };

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { originOrgId }: Props,
) => ({
  actions: {
    loadMergeIntegration: () => dispatch(loadMergeIntegration(originOrgId)),
    loadOrgEmployees: (employeeStatus: EmployeeStatus) =>
      dispatch(loadOrgEmployeesByStatus(originOrgId, employeeStatus)),
    loadEmployeeGroups: () => dispatch(loadEmployeeGroups(originOrgId)),
    reassignEmployeeGroups: ({
      groupReassignments,
      explicitEmployeeAssignments,
      groupsToFilter,
    }: ReassignEmployeeGroupsRequest) =>
      dispatch(
        reassignEmployeeGroups({
          orgId: originOrgId,
          groupReassignments,
          explicitEmployeeAssignments,
          groupsToFilter,
        }),
      ),
    showGlobalErrorDialog: () =>
      dispatch(openOrCloseGlobalDialog(GLOBAL_DIALOGS.GLOBAL_ERROR.key, true)),
  },
});

type Props = {
  originOrgId: number;
};

export const MultiOrgIntegrationConfig = ({ originOrgId }: Props) => {
  const {
    mergeIntegration,
    mergeIntegrationState,
    orgs,
    employeeById,
    employeesForGroupId,
    employees,
    employeesStates,
    employeeGroups,
    employeeGroupsState,
    user,
  } = useSelector(mapStateToProps({ originOrgId }), shallowEqual);
  const { actions } = mapDispatchToProps(useDispatch(), { originOrgId });
  const history = useHistory();

  const [isIncludingEmptyGroups, setIsIncludingEmptyGroups] = useState<boolean>(false);

  const [tab, setTab] = useState<0 | 1 | 2>(0);
  const [groupReassignments, setGroupReassignments] = useState<{
    [employeeGroupId: number]: number;
  }>({});
  const [assignmentConflicts, setAssignmentConflicts] = useState<{
    [employeeId: number]: Set<number>;
  }>({});
  const [explicitEmployeeAssignments, setExplicitEmployeeAssignments] = useState<{
    [employeeId: number]: number;
  }>({});

  useEffect(() => {
    if (user.isAccountAdmin) {
      actions.loadMergeIntegration();
      actions.loadOrgEmployees(EMPLOYEE_STATUS.CURRENT);
      actions.loadOrgEmployees(EMPLOYEE_STATUS.FORMER);
      actions.loadEmployeeGroups();
    }
  }, [actions, user.isAccountAdmin]);

  const employeesAreLoading = employeesStates.every(
    (requestState: RequestState) => !requestState || requestState.loading,
  );
  if (
    !mergeIntegrationState ||
    mergeIntegrationState.loading ||
    !employeeGroupsState ||
    employeeGroupsState.loading ||
    employeesAreLoading
  ) {
    return <LoadingMessage />;
  }

  if (!mergeIntegration) {
    history.push(employeesRoute(originOrgId));
  }

  const handleTabChange = (_event: React.ChangeEvent<{}>, value: 0 | 1 | 2) => {
    setTab(value);
  };

  const handleGroupAssignment =
    (groupId: number) =>
    (event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      const reassignedOrg =
        event.target.value === 'None' ? undefined : (event.target.value as number);

      let newGroupReassignments: { [employeeGroupId: number]: number };
      if (reassignedOrg) {
        newGroupReassignments = {
          ...groupReassignments,
          [groupId]: reassignedOrg,
        };
      } else {
        const { [groupId]: _, ...rest } = groupReassignments;
        newGroupReassignments = {
          ...rest,
        };
      }

      const newAssignmentConflicts: { [employeeId: number]: Set<number> } = {};
      let newExplicitEmployeeAssignments: { [employeeGroupId: number]: number } = {};
      employees.forEach((e) => {
        const orgReassignments = e.EmployeeGroups.reduce((set, g) => {
          if (newGroupReassignments[g.employeeGroupId]) {
            set.add(newGroupReassignments[g.employeeGroupId]);
          }
          return set;
        }, new Set<number>());

        if (orgReassignments.size > 1) {
          newAssignmentConflicts[e.employeeId] = orgReassignments;
        } else if (orgReassignments.size === 1) {
          const { [e.employeeId]: _, ...rest } = explicitEmployeeAssignments;
          newExplicitEmployeeAssignments = {
            ...rest,
          };
        }
      });

      setGroupReassignments(newGroupReassignments);
      setAssignmentConflicts(newAssignmentConflicts);
      setExplicitEmployeeAssignments(newExplicitEmployeeAssignments);
    };

  const handleExplicitEmployeeAssignment =
    (employeeId: number) =>
    (event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      if (event.target.value === 'None') {
        const { [employeeId]: _, ...newExplicitEmployeeAssignments } = explicitEmployeeAssignments;
        setExplicitEmployeeAssignments(newExplicitEmployeeAssignments);
      } else {
        setExplicitEmployeeAssignments({
          ...explicitEmployeeAssignments,
          [employeeId]: event.target.value as number,
        });
      }
    };

  const handleSaveConfiguration = async () => {
    if (hasAssignmentConflicts) {
      setTab(1);
      return;
    }

    const groupsToFilter = employeeGroups
      .filter(({ employeeGroupId }) => !groupReassignments[employeeGroupId])
      .map(({ employeeGroupId }) => employeeGroupId);

    const groupReassignmentsPayload = Object.entries(groupReassignments).map(
      ([employeeGroupId, orgId]) => ({
        employeeGroupId: parseInt(employeeGroupId, 10),
        orgId,
        originOrgId,
      }),
    );

    const resp = await actions.reassignEmployeeGroups({
      groupReassignments: groupReassignmentsPayload,
      explicitEmployeeAssignments,
      groupsToFilter,
    });
    if (!resp.error) {
      history.push(employeesRoute(originOrgId));
    } else {
      actions.showGlobalErrorDialog();
    }
  };

  const groupReassignmentsLength = Object.entries(groupReassignments).length;
  const assignmentConflictsLength = Object.entries(assignmentConflicts).length;
  const explicitAssignmentsLength = Object.entries(explicitEmployeeAssignments).length;
  const hasAssignmentConflicts = assignmentConflictsLength !== explicitAssignmentsLength;
  const cannotSaveConfiguration =
    (!groupReassignmentsLength && tab === 0) || (hasAssignmentConflicts && tab === 1);

  const TabComponents = [
    <GroupAssignmentTab
      orgs={orgs}
      employeeGroups={employeeGroups}
      employeesForGroupId={employeesForGroupId}
      groupReassignments={groupReassignments}
      handleGroupAssignment={handleGroupAssignment}
      isIncludingEmptyGroups={isIncludingEmptyGroups}
      setIsIncludingEmptyGroups={setIsIncludingEmptyGroups}
    />,
    <ResolveConflictsTab
      orgs={orgs}
      assignmentConflicts={assignmentConflicts}
      explicitEmployeeAssignments={explicitEmployeeAssignments}
      employeeById={employeeById}
      handleExplicitEmployeeAssignment={handleExplicitEmployeeAssignment}
    />,
    <PreviewImportTab
      originOrgId={originOrgId}
      employeesForGroupId={employeesForGroupId}
      employeeById={employeeById}
      orgs={orgs}
      groupReassignments={groupReassignments}
      explicitEmployeeAssignments={explicitEmployeeAssignments}
    />,
  ];

  return (
    <div className="u-position-top-sticky u-background-multi-org-integration-config">
      <UiBreadcrumbHeader
        pageTitle="Multiple Organization HR/Payroll Integration Configuration"
        backToPage={employeesRoute(originOrgId)}
        customPrimaryAction={
          <Tooltip
            title={
              groupReassignmentsLength
                ? hasAssignmentConflicts
                  ? 'Assign employees to organizations before proceeding'
                  : ''
                : 'Assign groups to organizations before proceeding'
            }
          >
            <span>
              <UiButton
                variant="primary"
                onClick={handleSaveConfiguration}
                disabled={cannotSaveConfiguration}
              >
                {!hasAssignmentConflicts || tab === 1 ? 'Save Configuration' : 'Resolve Conflicts'}
              </UiButton>
            </span>
          </Tooltip>
        }
      />
      <div className="u-margin-lg">
        <div className="typo-subtitle">
          If you have multiple organizations in Team Engine but one HR/payroll system, you can use
          this configuration to specify which groups — and their members — should be assigned to
          certain organizations.
        </div>
      </div>
      <div className="u-margin-x-lg">
        <AppBar position="sticky" color="default">
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label="Assign groups" />
            <Tab label="Resolve conflicts" disabled={!assignmentConflictsLength} />
            <Tab label="Preview import" disabled={cannotSaveConfiguration} />
          </Tabs>
        </AppBar>
        {TabComponents[tab]}
      </div>
    </div>
  );
};
